import React, { useEffect, useState } from 'react';
import { number } from 'prop-types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { parse } from '../../../util/urlHelpers';
import omit from 'lodash/omit';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createInstance } from '../../../util/sdkLoader';
import appSettings from '../../../config/settings';
import config from '../../../config';
import { convertUnitToSubUnit, unitDivisor } from '../../../util/currency';
import { categoryAccentColor } from '../../../util/categoryColors';

import css from './MainPanelHeader.module.css';

/**
 * Category pill strip — the primary entry point for the `category` publicData
 * filter, rebuilt to match the redesign prototype
 * (new-search-ui/socialbnb-search-desktop-mobile.html → "category pills").
 *
 * It is intentionally decoupled from the SearchPage container props: it reads
 * the current selection from the URL and writes it back through the SAME
 * search param the existing SelectMultipleFilter uses.
 *
 * Param wiring (mirrors FilterComponent → SelectMultipleFilter for the
 * `category` enum field, scope `public`):
 *   - Query param name: `pub_category`
 *     (constructQueryParamName('category', 'public') === 'pub_category').
 *   - Value format: `has_any:` + comma-joined values, e.g.
 *     `pub_category=has_any:kultur-soziales`. The `category` field is stored as
 *     an ARRAY (multi-enum) on listings, so the API requires the `has_any:`
 *     operator — a bare `pub_category=x` matches nothing.
 *   - Reading: `parseSelected` strips the `has_any:`/`has_all:` prefix and splits
 *     on commas, so the value round-trips correctly.
 *
 * The option keys mirror configListing.js `category` enumOptions EXACTLY so the
 * filter actually works against real listing data. The per-pill accent colour
 * and line icon come from the prototype's visual language; the count badge is a
 * real total fetched once via an anonymous SDK query (see useCategoryCounts).
 */

const QUERY_PARAM = 'pub_category';

// Inline line-icons (stroke) — keyed per category. Mirrors the prototype's
// `icon()` SVG paths so the strip reads as small friendly glyphs, not photos.
const ICON_PATHS = {
  all:
    '<rect x="3" y="3" width="7" height="7" rx="1.6"/><rect x="14" y="3" width="7" height="7" rx="1.6"/><rect x="3" y="14" width="7" height="7" rx="1.6"/><rect x="14" y="14" width="7" height="7" rx="1.6"/>',
  // Meeresschutz / marine — waves
  marine:
    '<path d="M2 7c1.6-1.6 3.2-1.6 4.8 0s3.2 1.6 4.8 0 3.2-1.6 4.8 0 3.2 1.6 4.8 0"/><path d="M2 12c1.6-1.6 3.2-1.6 4.8 0s3.2 1.6 4.8 0 3.2-1.6 4.8 0 3.2 1.6 4.8 0"/><path d="M2 17c1.6-1.6 3.2-1.6 4.8 0s3.2 1.6 4.8 0 3.2-1.6 4.8 0 3.2 1.6 4.8 0"/>',
  // Tierschutz / wildlife — paw
  wildlife:
    '<circle cx="6.2" cy="9.5" r="1.7"/><circle cx="10.4" cy="6.4" r="1.7"/><circle cx="15" cy="6.4" r="1.7"/><circle cx="18.6" cy="9.5" r="1.7"/><path d="M8.2 16.4c0-2.3 1.7-3.9 3.8-3.9s3.8 1.6 3.8 3.9-1.7 3.4-3.8 3.4-3.8-1.1-3.8-3.4z"/>',
  // Naturschutz / nature — leaf
  nature:
    '<path d="M5 19C5 11 11 5 19 5c0 8-6 14-14 14z"/><path d="M5.5 18.5C9 15 12.5 11.5 16 9"/>',
  // Kultur & Soziales / culture — two people
  culture:
    '<circle cx="9" cy="8" r="2.4"/><circle cx="16" cy="9" r="2"/><path d="M4.5 19c0-3 2-4.6 4.5-4.6s4.5 1.6 4.5 4.6"/><path d="M13.6 19c0-2.4 1.5-3.8 3.6-3.8 1 0 1.9.3 2.6.9"/>',
  // Aktiv & Abenteuer / adventure — mountain
  adventure:
    '<path d="M3 19l6-11 4 6.5 2-3 6 7.5z"/><circle cx="17.5" cy="6.5" r="1.5"/>',
};

const CategoryIcon = ({ id }) => (
  <span
    className={css.categoryIcon}
    aria-hidden="true"
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{
      __html: `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${ICON_PATHS[id] || ''}</svg>`,
    }}
  />
);

// `key` is the pill's stable id (matches the color map in categoryColors.js).
// `values` are the `pub_category` values a pill matches, sent to the API
// comma-joined behind `has_any:`. Every listing carries the unified lowercase
// slug, so each pill matches exactly one value; the array shape is kept so a
// pill can absorb an alias without a signature change.
// `key: null` is the "Alle" pill (clears the category param).
//
// One pill per category in the taxonomy — the same five the `category` listing
// field offers in configListing.js, so the strip and the SelectMultipleFilter
// can never disagree about what is filterable.
const CATEGORY_OPTIONS = [
  { key: null, labelId: 'MainPanelHeader.categoryAll', iconId: 'all' },
  { key: 'meeresschutz', values: ['meeresschutz'], labelId: 'filter.category.marine', iconId: 'marine' },
  { key: 'tierschutz', values: ['tierschutz'], labelId: 'filter.category.animalCare', iconId: 'wildlife' },
  { key: 'naturschutz', values: ['naturschutz'], labelId: 'filter.category.environment', iconId: 'nature' },
  { key: 'kultur-soziales', values: ['kultur-soziales'], labelId: 'filter.category.culture', iconId: 'culture' },
  { key: 'aktiv-abenteuer', values: ['aktiv-abenteuer'], labelId: 'filter.category.adventure', iconId: 'adventure' },
];

// The pills we fetch counts for (exclude the "Alle" pseudo-option).
const COUNTABLE_OPTIONS = CATEGORY_OPTIONS.filter(o => o.key);

// A pill is active when the URL's selected values include ANY of the pill's
// values. Values are unique per pill, so there's no cross-match.
const isOptionSelected = (option, selected) =>
  (option.values || []).some(v => selected.includes(v));

// Parse the currently selected category keys out of the URL search string.
const parseSelected = search => {
  const { [QUERY_PARAM]: raw } = parse(search);
  if (!raw) {
    return [];
  }
  // `category` is a multi-enum, so the value is `has_any:` + a comma-joined
  // list. Guard the prefix anyway so a bare value still parses.
  const stripped =
    raw.indexOf('has_all:') === 0 || raw.indexOf('has_any:') === 0 ? raw.substring(8) : raw;
  return stripped.split(',').filter(Boolean);
};

// Convert a `min,max` price URL value into the subunit `price` filter the API
// expects — identical to the transform the main search (SearchPage.duck) applies,
// so the counts sit in the same universe as the results.
const priceSearchParam = priceParam => {
  const inSubunits = value =>
    convertUnitToSubUnit(value, unitDivisor(config?.currencyConfig?.currency));
  const values = priceParam ? priceParam.split(',') : [];
  return priceParam && values.length === 2
    ? { price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(',') }
    : {};
};

// Build the listing-query filters that mirror the CURRENT search — listing type,
// location, price and any other active filters — but WITHOUT the category facet,
// pagination or sort. A per-category count then reflects the same result universe
// as the "Alle" total (e.g. on the "Stays" tab a category badge only counts
// stays), instead of a global figure that can exceed the total.
const buildContextParams = search => {
  // Same parse options as the main search so `bounds`/`origin` become the SDK
  // types the listings query expects (not raw strings).
  const parsed = parse(search, { latlng: ['origin'], latlngBounds: ['bounds'] });
  const { price, bounds, country, continent } = parsed;

  // Drop non-filter params (pagination/sort/display) and the ones we re-add in a
  // transformed shape below (price → subunits, country/continent → pub_*). The
  // category facet itself is dropped so each badge counts independently.
  const rest = omit(parsed, [
    'page',
    'perPage',
    'per_page',
    'sort',
    'mapSearch',
    'address',
    'dates',
    'price',
    'origin',
    'bounds',
    'country',
    'continent',
    QUERY_PARAM,
  ]);

  return {
    // pub_listingType, pub_food, keywords and any other active pub_* filters.
    ...rest,
    ...priceSearchParam(price),
    ...(bounds ? { bounds } : {}),
    ...(country ? { pub_country: country } : {}),
    ...(continent ? { pub_continent: continent } : {}),
  };
};

// Stable cache/dependency key for a search context, derived from the RAW string
// params (not the SDK-typed bounds object, which doesn't JSON-serialize stably).
// Excludes pagination/sort/display and the category facet so only real context
// changes (listing type, location, other filters) trigger a refetch.
const contextSignature = search => {
  const parsed = parse(search);
  const filters = omit(parsed, [
    'page',
    'perPage',
    'per_page',
    'sort',
    'mapSearch',
    'address',
    'dates',
    'origin',
    QUERY_PARAM,
  ]);
  return JSON.stringify(filters);
};

/**
 * Fetches a per-category result count (and an "all" total) that reflects the
 * current search context, via a lightweight anonymous SDK instance. Each badge
 * equals how many results you'd get if you selected that category on top of the
 * active search — so the numbers stay consistent with the "Alle"/results total.
 *
 * Results are cached per context signature so switching filters back and forth
 * doesn't refetch, but a new context (new filters/location/tab) recomputes.
 */
const countsCacheBySignature = new Map();
const promisesBySignature = new Map();

const fetchCategoryCounts = search => {
  const contextParams = buildContextParams(search);
  const signature = contextSignature(search);

  if (countsCacheBySignature.has(signature)) {
    return Promise.resolve(countsCacheBySignature.get(signature));
  }
  if (promisesBySignature.has(signature)) {
    return promisesBySignature.get(signature);
  }
  if (!appSettings.sdk.clientId) {
    return Promise.resolve(null);
  }

  const sdk = createInstance({
    clientId: appSettings.sdk.clientId,
    secure: appSettings.usingSSL,
    ...(appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {}),
    ...(appSettings.sdk.assetCdnBaseUrl
      ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
      : {}),
  });

  // Minimal payload: we only need meta.totalItems, which is independent of page size.
  const baseQuery = { perPage: 1, 'fields.listing': ['title'], 'fields.image': [] };
  const totalFor = extraParams =>
    sdk.listings
      .query({ ...baseQuery, ...contextParams, ...extraParams })
      .then(res => res?.data?.meta?.totalItems ?? 0)
      .catch(() => 0);

  const promise = Promise.all([
    totalFor({}),
    ...COUNTABLE_OPTIONS.map(o => totalFor({ pub_category: `has_any:${o.values.join(',')}` })),
  ])
    .then(([all, ...perCategory]) => {
      const byKey = { all };
      COUNTABLE_OPTIONS.forEach((o, i) => {
        byKey[o.key] = perCategory[i];
      });
      countsCacheBySignature.set(signature, byKey);
      promisesBySignature.delete(signature);
      return byKey;
    })
    .catch(() => {
      promisesBySignature.delete(signature);
      return null;
    });

  promisesBySignature.set(signature, promise);
  return promise;
};

const useCategoryCounts = search => {
  // Key the fetch off the current search context so the badges recompute when the
  // listing type / location / filters change (but not on plain pagination).
  const signature = contextSignature(search);
  const [counts, setCounts] = useState(() => countsCacheBySignature.get(signature) || null);

  useEffect(() => {
    let mounted = true;
    // Show the cached counts for this context immediately if we have them, so
    // there's no flash; otherwise keep the previous badges until the new ones load.
    const cached = countsCacheBySignature.get(signature);
    if (cached) {
      setCounts(cached);
    }
    fetchCategoryCounts(search).then(result => {
      if (mounted && result) {
        setCounts(result);
      }
    });
    return () => {
      mounted = false;
    };
  }, [signature]);

  return counts;
};

const CategoryStrip = ({ totalResults }) => {
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const { lang } = useParams();
  const counts = useCategoryCounts(location.search);

  const selected = parseSelected(location.search);
  const noneSelected = selected.length === 0;

  const applySelection = nextSelected => {
    // Merge with the rest of the current query params, resetting pagination.
    const currentParams = parse(location.search);
    const { page, ...restParams } = currentParams;
    const nextParams = {
      ...restParams,
      // `category` is stored as an ARRAY (multi-enum) on listings, so the API
      // needs the `has_any:` operator — a bare `pub_category=x` matches nothing
      // against array data. `has_any:` returns listings whose category array
      // contains any of the pill's values (new slug + legacy key).
      [QUERY_PARAM]: nextSelected.length > 0 ? `has_any:${nextSelected.join(',')}` : null,
    };

    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration, { lang }, nextParams)
    );
  };

  const selectAll = () => {
    if (!noneSelected) {
      applySelection([]);
    }
  };

  const toggleCategory = option => {
    // Single-select: a category replaces any previous selection; clicking the
    // already-active chip clears back to "Alle".
    const nextSelected = isOptionSelected(option, selected) ? [] : option.values;
    applySelection(nextSelected);
  };

  const formatCount = n => (typeof n === 'number' ? n.toLocaleString('de-DE') : null);

  return (
    <nav
      className={css.categoryStrip}
      aria-label="Kategorien"
      data-elb="searchp"
      data-elb-searchp="name:category-strip;"
    >
      <ul className={css.categoryList}>
        {CATEGORY_OPTIONS.map(option => {
          const isAll = option.key === null;
          const isSelected = isAll ? noneSelected : isOptionSelected(option, selected);
          // The "Alle" pill mirrors the current results total (the "X Ergebnisse"
          // count), so it matches the results header and the impact banner.
          // Falls back to the global count while loading / when unavailable.
          const count = isAll
            ? typeof totalResults === 'number' && totalResults > 0
              ? totalResults
              : counts?.all
            : counts?.[option.key];
          const displayCount = formatCount(count);

          return (
            <li key={option.key || 'all'} className={css.categoryItem}>
              <button
                type="button"
                className={classNames(css.categoryButton, {
                  [css.categoryButtonSelected]: isSelected,
                })}
                style={isSelected ? { '--sbCatAccent': categoryAccentColor(option.key) } : undefined}
                aria-pressed={isSelected}
                onClick={() => (isAll ? selectAll() : toggleCategory(option))}
                data-elbaction={`click: click category-${option.key || 'all'}`}
              >
                <CategoryIcon id={option.iconId} />
                <span className={css.categoryLabel}>
                  <FormattedMessage id={option.labelId} />
                </span>
                {displayCount != null ? (
                  <span className={css.categoryCount}>{displayCount}</span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

CategoryStrip.defaultProps = {
  totalResults: null,
};

CategoryStrip.propTypes = {
  // Current search results total; drives the "Alle" pill count when > 0.
  totalResults: number,
};

export default CategoryStrip;
